/**
 * Bound a promise in wall-clock time.
 *
 * The case this exists for is NOT a promise that rejects — that already
 * unwinds fine through a normal try/catch. It is a promise that *never
 * settles*. `indexedDB.open()` on a corrupted database fires no `success`,
 * no `error`, and no `blocked` event; the promise idb wraps around it stays
 * pending forever, and an `await` on it walks straight past any catch block.
 *
 * `Promise.race` is the fix: the timer settles the race even though the
 * work never will.
 */

const TIMEOUT_FLAG = '__fieldiqTimeout';

export interface TimeoutError extends Error {
  __fieldiqTimeout: true;
  timeoutMs: number;
}

/**
 * Type guard for the error `withTimeout` rejects with.
 *
 * This is a property check rather than `instanceof` on purpose: tsconfig
 * targets es5, where subclassing a built-in like Error breaks the prototype
 * chain and `instanceof MyError` silently returns false. A flag survives
 * downlevelling.
 */
export function isTimeoutError(err: unknown): err is TimeoutError {
  return !!err && (err as any)[TIMEOUT_FLAG] === true;
}

function timeoutError(label: string, ms: number): TimeoutError {
  const err = new Error(`${label} did not settle within ${ms}ms`) as TimeoutError;
  err.name = 'TimeoutError';
  err.__fieldiqTimeout = true;
  err.timeoutMs = ms;
  return err;
}

/**
 * Resolve/reject with `work`, or reject with a TimeoutError after `ms`.
 *
 * `work` is NOT cancelled on timeout — you cannot cancel an IndexedDB request.
 * It keeps running, so callers that care whether it eventually finished should
 * hold onto the original promise (see `purgeTenantData`).
 */
export function withTimeout<T>(work: Promise<T>, ms: number, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;

  // If `work` rejects *after* we've already given up on it, nothing is left
  // attached to observe that rejection and it surfaces as an unhandled
  // rejection (a console error in the browser, a failed test run in Jest).
  // This no-op handler is a separate branch of the chain — it does not
  // affect what the race below sees.
  work.catch(() => {});

  const timeout = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => reject(timeoutError(label, ms)), ms);
  });

  return Promise.race([work, timeout]).then(
    (value) => {
      if (timer !== undefined) clearTimeout(timer);
      return value;
    },
    (err) => {
      if (timer !== undefined) clearTimeout(timer);
      throw err;
    }
  );
}
